import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { BrnDialogDescription, BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { PENDING_HINT } from '../domain/entities/design.models';

export interface PartnerOrderStatusDialogContext {
  packName: string;
  /** A rental at a studio without a shop: the photographer pays the studio by hand. */
  manual: boolean;
  /** The studio's shop, or its Telegram on the manual lane. Empty — the studio published no link, no button. */
  actionUrl: string;
  /** Opens the ready cancel flow. It lands on the SAME ViewContainerRef, so this dialog must not close first. */
  cancelOrder: () => void;
}

/** Which of the three steps the order stands on — the middle one, always: the request is placed, the
 *  payment is not confirmed yet. */
type OrderStepState = 'done' | 'current' | 'next';

interface OrderStep {
  readonly label: string;
  readonly state: OrderStepState;
}

/**
 * «Ждёт оплаты» opened from the card. A pending order has no spreads to show — the studio has not
 * confirmed the payment, so the pack is not readable yet; what the photographer needs instead is where
 * the order stands, how to finish paying, and the way back out of it.
 */
@Component({
  selector: 'app-partner-order-status-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, DialogSheetComponent, IconComponent, BrnDialogTitle, BrnDialogDescription],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 brnDialogTitle class="mb-5 text-lg leading-snug font-bold text-ink">{{ title }}</h2>

        <!-- The wizards' own stepper shape — dot, connector, label — so the wait reads as a place in a
             process the photographer has already walked, not as a new kind of screen. -->
        <div>
          @for (step of steps; track step.label; let first = $first) {
            <div class="relative flex items-center gap-3.5 py-2.75 text-c3">
              @if (!first) {
                <span
                  class="absolute left-[var(--inset-node-line-left)] top-[var(--inset-node-line-top)]
                         h-[var(--size-node-line-height)] w-[var(--size-node-line-width)] -translate-y-full"
                  [class]="step.state === 'done' ? 'bg-acc-line' : 'bg-line2'"
                ></span>
              }
              <span
                class="relative z-1 grid size-8 flex-none place-items-center rounded-full text-sm font-medium
                       leading-none border-(length:--border-width-emphasis) transition-colors duration-[var(--t-fast)]"
                [class]="dotClass(step.state)"
              >
                @if (step.state === 'done') {
                  <app-icon name="check" [size]="15" />
                } @else {
                  {{ $index + 1 }}
                }
              </span>
              <span class="text-sm leading-compact" [class]="labelClass(step.state)">{{ step.label }}</span>
            </div>
          }
        </div>

        <p brnDialogDescription class="mt-4.5 text-base leading-relaxed text-ink">{{ note }}</p>

        <div class="mt-7 flex flex-col gap-3">
          @if (ctx.actionUrl) {
            <button hlmBtn type="button" class="h-12 w-full justify-center" (click)="openAction()">
              {{ actionLabel }}
            </button>
          }
          <div class="flex gap-3">
            <button hlmBtn type="button" variant="outline" class="h-12 flex-1 justify-center" (click)="cancelOrder()">
              Отменить заказ
            </button>
            <button hlmBtn type="button" variant="outline" class="h-12 flex-1 justify-center" (click)="close()">Закрыть</button>
          </div>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class PartnerOrderStatusDialogComponent {
  protected readonly hostClass = dialogSheetHostClass('xs', 'p-9');
  protected readonly ctx = injectBrnDialogContext<PartnerOrderStatusDialogContext>();
  private readonly dialogRef = inject(BrnDialogRef);

  protected readonly title = `Заказ «${this.ctx.packName}»`;

  protected readonly steps: readonly OrderStep[] = this.ctx.manual
    ? [
        { label: 'Заявка отправлена', state: 'done' },
        { label: 'Оплата студии напрямую', state: 'current' },
        { label: 'Студия начислит кредит', state: 'next' },
      ]
    : [
        { label: 'Заказ оформлен', state: 'done' },
        { label: 'Оплата в магазине студии', state: 'current' },
        { label: 'Шаблон в «Моих шаблонах»', state: 'next' },
      ];

  protected readonly note = this.ctx.manual
    ? 'Студия сообщит нам об оплате. Начислим кредит — шаблон добавится сам.'
    : PENDING_HINT;

  protected readonly actionLabel = this.ctx.manual ? 'Написать студии' : 'Открыть магазин студии';

  protected dotClass(state: OrderStepState): string {
    if (state === 'current') return 'border-acc bg-acc text-on-acc shadow-[var(--shadow-focus-soft)]';
    if (state === 'done') return 'border-transparent bg-acc-soft text-acc2';
    return 'border-line2 bg-panel text-c3';
  }

  protected labelClass(state: OrderStepState): string {
    if (state === 'current') return 'text-ink font-semibold';
    return state === 'done' ? 'text-c2 font-normal' : 'text-c3 font-normal';
  }

  /** The payment lives outside our app — the shop (or the studio's chat) opens beside it, never in place. */
  protected openAction(): void {
    window.open(this.ctx.actionUrl, '_blank', 'noopener');
  }

  // Окно отмены встаёт ПОВЕРХ этого: Spartan рушит вью закрытого диалога только после exit-анимации и
  // уносит с собой всё, что успели открыть на том же ViewContainerRef. Закрыться — забота вызывающего.
  protected cancelOrder(): void {
    this.ctx.cancelOrder();
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
